import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Chip, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { TextSetting } from './BlockSettings';
import { humanise, memberLabel } from './format';
import { Actions, Choice, PanelHeader, Row, SwitchRow, TextInput } from './settingsKit';
import { type SeriesOverride, seriesOverrides } from './seriesStyle';
import type { Block, LensDocumentData } from './types';
import type { BoardEditor } from './useBoardEdit';

type PartSettingsProps = { block: Block; part: string; data?: LensDocumentData; editor: BoardEditor; onSelect?: (id: string) => void };

const SWATCHES = [1, 2, 3, 4, 5, 6, 7, 8];
const AUTO = 'auto';

const SWATCH_GROUP = {
  flexWrap: 'wrap',
  gap: 0.5,
  '& .MuiToggleButtonGroup-grouped': { border: 0, borderRadius: '50% !important', m: 0 },
  '& .MuiToggleButton-root': { height: 26, minWidth: 26, p: 0.5, '&.Mui-selected': { bgcolor: 'transparent', boxShadow: 'inset 0 0 0 2px currentColor' } },
  '& .MuiToggleButton-root.text': { borderRadius: '13px !important', fontSize: 12, px: 1, textTransform: 'none' },
} as const;

function ComboSeries({ block, name, editor }: { block: Block; name: string; editor: BoardEditor }) {
  const marks = (block.marks ?? {}) as Record<string, string>;
  const right = Array.isArray(block.right) ? (block.right as Array<string>) : [];
  const measures = Array.isArray(block.combo) ? (block.combo as Array<string>) : [];
  const shape = marks[name] ?? (measures.findIndex((measure) => measure === name || measure.endsWith(`.${name}`)) === 0 ? 'bar' : 'line');
  return (
    <>
      <Choice
        choices={[
          ['bar', 'Bars'],
          ['line', 'Line'],
          ['area', 'Area'],
        ]}
        disabled={editor.pending}
        label="Drawn as"
        onChange={(next) => editor.apply({ op: 'set', id: block.id, patch: { marks: { [name]: next } } })}
        value={shape}
      />
      <Choice
        choices={[
          ['left', 'Main'],
          ['right', 'Its own, below'],
        ]}
        disabled={editor.pending}
        label="Scale"
        onChange={(next) => editor.apply({ op: 'set', id: block.id, patch: { right: next === 'right' ? [...right.filter((entry) => entry !== name), name] : right.length > 1 ? right.filter((entry) => entry !== name) : null } })}
        value={right.includes(name) ? 'right' : 'left'}
      />
    </>
  );
}

function SeriesStyle({ block, name, label, editor }: { block: Block; name: string; label: string; editor: BoardEditor }) {
  const current: SeriesOverride = seriesOverrides(block)[name] ?? {};
  const set = (patch: Record<string, unknown>) => editor.apply({ op: 'set', id: block.id, patch: { series: { [name]: patch } } });
  return (
    <>
      {'combo' in block ? <ComboSeries block={block} editor={editor} name={name} /> : null}
      <TextSetting label="Label" onCommit={(text) => set({ label: text || null })} placeholder={label} value={current.label ?? ''} />
      <Row label="Colour" set={current.color !== undefined}>
        <ToggleButtonGroup
          aria-label="Colour"
          className="lens-doc"
          disabled={editor.pending}
          exclusive
          onChange={(_, next: string | null) => next !== null && set({ color: next === AUTO ? null : next === 'muted' ? 'muted' : Number(next) })}
          size="small"
          sx={SWATCH_GROUP}
          value={current.color === undefined ? AUTO : String(current.color)}
        >
          <ToggleButton className="text" value={AUTO}>
            Auto
          </ToggleButton>
          {SWATCHES.map((index) => (
            <ToggleButton aria-label={`Colour ${index}`} key={index} sx={{ color: `var(--chart-${index})` }} value={String(index)}>
              <Box component="span" sx={{ bgcolor: `var(--chart-${index})`, borderRadius: '50%', display: 'block', height: 16, width: 16 }} />
            </ToggleButton>
          ))}
          <ToggleButton className="text" value="muted">
            Grey
          </ToggleButton>
        </ToggleButtonGroup>
      </Row>
      <SwitchRow checked={Boolean(current.hidden)} disabled={editor.pending} label="Hidden" onChange={(hidden) => set({ hidden: hidden || null })} />
    </>
  );
}

function memberDimension(block: Block, data?: LensDocumentData): string | undefined {
  const planned = data?.blocks[block.id];
  const id = planned?.queries?.[0] ?? planned?.query;
  return data?.queries[id ?? '']?.columns.find((column) => column.role === 'dim')?.name;
}

function MemberSettings({ block, member, data, editor }: { block: Block; member: string; data?: LensDocumentData; editor: BoardEditor }) {
  const dim = memberDimension(block, data);
  const order = Array.isArray(block.order) ? (block.order as Array<string>) : [];
  const where = (block.where ?? {}) as Record<string, Array<string> | { not: Array<string> }>;
  const excluded = dim && !Array.isArray(where[dim]) ? (where[dim] as { not?: Array<string> } | undefined)?.not ?? [] : [];
  const kept = dim && Array.isArray(where[dim]) ? (where[dim] as Array<string>) : [];
  return (
    <>
      <SeriesStyle block={block} editor={editor} label={memberLabel(member)} name={member} />
      <Actions>
        <Button disabled={editor.pending} onClick={() => editor.apply({ op: 'set', id: block.id, patch: { order: [member, ...order.filter((entry) => entry !== member)] } })} size="small" variant="text">
          Pin to the top
        </Button>
        {order.includes(member) ? (
          <Button disabled={editor.pending} onClick={() => editor.apply({ op: 'set', id: block.id, patch: { order: order.length > 1 ? order.filter((entry) => entry !== member) : null } })} size="small" variant="text">
            Unpin
          </Button>
        ) : null}
        {dim ? (
          <Button disabled={editor.pending || kept.includes(member)} onClick={() => editor.apply({ op: 'set', id: block.id, patch: { where: { ...where, [dim]: [...kept, member] } } })} size="small" variant="text">
            {kept.length ? 'Keep this too' : 'Keep only this'}
          </Button>
        ) : null}
        {dim ? (
          <Button disabled={editor.pending} onClick={() => editor.apply({ op: 'set', id: block.id, patch: { where: { ...where, [dim]: kept.length ? kept.filter((entry) => entry !== member) : { not: [...excluded, member] } } } })} size="small" variant="text">
            Leave out of the data
          </Button>
        ) : null}
      </Actions>
    </>
  );
}

type Ref = { value: number; label?: string; axis?: 'right' };

type Range = { min?: number; max?: number; zero?: boolean };

const X_LABELS: Array<[string, string]> = [
  ['auto', 'As they fit'],
  ['all', 'Every one'],
  ['angled', 'Angled'],
];

function RangeSettings({ label, range, onChange }: { label: string; range: Range; onChange: (patch: Record<string, unknown>) => void }) {
  const number = (text: string) => (text.trim() === '' || !Number.isFinite(Number(text)) ? null : Number(text));
  return (
    <Row label={label} set={range.min !== undefined || range.max !== undefined}>
      <TextInput label="Minimum" onCommit={(text) => onChange({ min: number(text) })} placeholder="Lowest: auto" value={range.min === undefined ? '' : String(range.min)} />
      <TextInput label="Maximum" onCommit={(text) => onChange({ max: number(text) })} placeholder="Highest: auto" value={range.max === undefined ? '' : String(range.max)} />
    </Row>
  );
}

function AxisSettings({ block, editor }: { block: Block; editor: BoardEditor }) {
  const [onSecond, setOnSecond] = useState(false);
  const scale = (block.scale ?? {}) as Range;
  const refs = (Array.isArray(block.refs) ? block.refs : []) as Array<Ref>;
  const set = (patch: Record<string, unknown>) => editor.apply({ op: 'set', id: block.id, patch });
  const number = (text: string) => (text.trim() === '' || !Number.isFinite(Number(text)) ? null : Number(text));
  const combo = 'combo' in block;
  return (
    <>
      <TextSetting label="Value axis" onCommit={(text) => set({ yTitle: text || null })} placeholder="The unit, when there is one" value={typeof block.yTitle === 'string' ? block.yTitle : ''} />
      <TextSetting label="Along the bottom" onCommit={(text) => set({ xTitle: text || null })} placeholder="None" value={typeof block.xTitle === 'string' ? block.xTitle : ''} />
      <Choice choices={X_LABELS} disabled={editor.pending} inherit="auto" label="Labels along the bottom" onChange={(next) => set({ xLabels: next === 'auto' ? null : next })} value={typeof block.xLabels === 'string' ? block.xLabels : 'auto'} />
      <RangeSettings label={combo ? 'Main scale' : 'Range'} onChange={(patch) => set({ scale: patch })} range={scale} />
      <SwitchRow checked={Boolean(scale.zero)} disabled={editor.pending} label="Start at zero" onChange={(zero) => set({ scale: { zero: zero || null } })} />
      {combo ? <RangeSettings label="Its own scale, below" onChange={(patch) => set({ scale2: patch })} range={(block.scale2 ?? {}) as Range} /> : null}
      <Row label="Reference lines" set={refs.length > 0} stacked>
        <Stack spacing={0.75} sx={{ width: '100%' }}>
          {refs.length ? (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {refs.map((ref, index) => (
                <Chip
                  deleteIcon={<CloseIcon aria-label={`Remove the line at ${ref.value}`} />}
                  key={index}
                  label={`${ref.label ?? 'Line'} at ${ref.value.toLocaleString('en-AU')}${ref.axis === 'right' ? ' (lower scale)' : ''}`}
                  onDelete={editor.pending ? undefined : () => set({ refs: refs.length > 1 ? refs.filter((_, at) => at !== index) : null })}
                  size="small"
                  sx={{ fontSize: 12.5 }}
                />
              ))}
            </Stack>
          ) : null}
          <TextInput
            label="Add a line at"
            onCommit={(text) => {
              const [value, ...words] = text.split(/\s+/);
              if (number(value) !== null) set({ refs: [...refs, { value: Number(value), ...(words.length ? { label: words.join(' ') } : {}), ...(combo && onSecond ? { axis: 'right' } : {}) }] });
            }}
            placeholder="Add a line: 4000000 Target"
            value=""
          />
        </Stack>
      </Row>
      {combo ? (
        <Choice
          choices={[
            ['main', 'Main scale'],
            ['right', 'Lower scale'],
          ]}
          label="Draw the new line on"
          onChange={(next) => setOnSecond(next === 'right')}
          value={onSecond ? 'right' : 'main'}
        />
      ) : null}
    </>
  );
}

function DimensionSettings({ block, name, editor }: { block: Block; name: string; editor: BoardEditor }) {
  const by = Array.isArray(block.by) ? block.by : [];
  const at = by.indexOf(name);
  const links = (block.links ?? {}) as Record<string, string>;
  const moved = (offset: number) => {
    const next = [...by];
    next.splice(at, 1);
    next.splice(at + offset, 0, name);
    return next;
  };
  return (
    <>
      <Actions>
        <Button disabled={editor.pending || at < 1} onClick={() => editor.apply({ op: 'set', id: block.id, patch: { by: moved(-1) } })} size="small" variant="text">
          Move left
        </Button>
        <Button disabled={editor.pending || at < 0 || at >= by.length - 1} onClick={() => editor.apply({ op: 'set', id: block.id, patch: { by: moved(1) } })} size="small" variant="text">
          Move right
        </Button>
        <Button color="error" disabled={editor.pending || at < 0} onClick={() => editor.apply({ op: 'set', id: block.id, patch: { by: by.length > 1 ? by.filter((entry) => entry !== name) : null } })} size="small" variant="text">
          Remove the column
        </Button>
      </Actions>
      <TextSetting
        label="Opens a page"
        onCommit={(link) => editor.apply({ op: 'set', id: block.id, patch: { links: { [name]: link || null } } })}
        placeholder={`https://admin.luxuryescapes.com/…/{${name}}`}
        value={links[name] ?? ''}
      />
    </>
  );
}

const COMPARED: Record<string, string> = { ly: 'last year', prior: 'the prior period', l2y: 'two years ago' };

function stopComparing(block: Block, data: LensDocumentData | undefined, editor: BoardEditor, name: string) {
  const planned = data?.blocks[block.id];
  const shown = Array.isArray(block.compare) ? (block.compare as Array<string>) : Object.keys(data?.queries[planned?.queries?.[0] ?? planned?.query ?? '']?.compare ?? {});
  const rest = shown.filter((entry) => entry !== name);
  editor.apply({ op: 'set', id: block.id, patch: { compare: rest.length ? rest : 'none' } });
}

export default function PartSettings({ block, part, data, editor, onSelect }: PartSettingsProps) {
  const [kind, name] = part.startsWith('@') ? [part, ''] : [part.slice(0, part.indexOf(':')), part.slice(part.indexOf(':') + 1)];
  const title = kind === '@axis' ? 'Axis' : kind === 'series' ? 'Series' : kind === 'dim' ? 'Column' : 'Member';
  return (
    <Box data-lens-part-settings={part} sx={{ px: 2, pb: 2, pt: 1.5 }}>
      <PanelHeader back={String(block.title ?? data?.blocks[block.id]?.name ?? 'Block')} onBack={() => onSelect?.(block.id)} title={name ? `${title} · ${kind === 'dim' ? humanise(name) : memberLabel(name)}` : title} />
      <Stack spacing={0.5}>
        {kind === 'series' && !(Array.isArray(block.by) && block.by.length) ? <SeriesStyle block={block} editor={editor} label={memberLabel(name)} name={name} /> : null}
        {kind === 'series' && COMPARED[name] ? (
          <Actions>
            <Button color="error" disabled={editor.pending} onClick={() => stopComparing(block, data, editor, name)} size="small" variant="text">
              {`Stop comparing with ${COMPARED[name]}`}
            </Button>
          </Actions>
        ) : null}
        {kind === 'member' || (kind === 'series' && Array.isArray(block.by) && block.by.length) ? <MemberSettings block={block} data={data} editor={editor} member={name} /> : null}
        {kind === '@axis' ? <AxisSettings block={block} editor={editor} /> : null}
        {kind === 'dim' ? <DimensionSettings block={block} editor={editor} name={name} /> : null}
      </Stack>
    </Box>
  );
}
